import { FC, ReactElement, useMemo } from 'react'
import { CommandItem } from '@/components/ui/command'
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip'
import formatNumber from '@/utils/format-number'
import isFungible from '@/utils/is-fungible'
import isRune from '@/utils/is-rune'
import isAlkanes from '@/utils/is-alkanes'
import InscriptionContent from '@/components/inscription-content'
import CollectionIcon from '@/components/collection-icon'
import UsedTransferBadge from '@/components/used-transfer-badge'
import Outpoint from '@/utils/outpoint'

import SelectButton from '../../components/select-button'

const USED_TRANSFER_SLUGS = ['brc-20', 'tap', 'rune']

const BaseItem: FC<{
	onSelect: () => void
	selected: boolean
	slug: string
	data: any
	children?: any
	/** Greyed out and unselectable (already listed, or sold and waiting to mine). */
	disabled?: boolean
}> = (props): ReactElement => {
	const { data, onSelect, selected, children, slug, disabled } = props
	const select = disabled ? () => {} : onSelect

	const itemCollectionSlug = data?.collection?.slug
	const isSlugFungible = isFungible(slug) || isRune(slug) || isAlkanes(slug)
	const isItemFungible = isFungible(slug) || isFungible(itemCollectionSlug)
	const isUsedTransfer = USED_TRANSFER_SLUGS.includes(itemCollectionSlug)

	const fungibleAmount = useMemo(() => {
		if (isItemFungible) return data?.meta?.attributes?.[0]?.value || data?.amount
		if (isUsedTransfer) return data?.meta?.attributes?.[0]?.value
		return null
	}, [data, isItemFungible, isUsedTransfer])

	const isValidTransfer = useMemo(() => {
		if (!isItemFungible || !data?.utxoOutpoint || !data?.inscription_id) return null
		try {
			const { txid } = Outpoint.decode(data.utxoOutpoint)
			return txid === data.inscription_id.split('i')[0]
		} catch {
			return null
		}
	}, [data, isItemFungible])

	const collectionName = data?.collection?.name

	return (
		<CommandItem
			value={data.outpoint}
			onSelect={select}
			disabled={disabled}
			aria-disabled={disabled || undefined}
			className={`data=[cmdk-item]:py-0.5 ${disabled ? 'opacity-50 data-[disabled]:opacity-50' : ''}`}
		>
			<div className="flex flex-col gap-2 sm:flex-row sm:items-center justify-between w-full min-w-0">
				<div className="flex gap-2 items-center">
					<SelectButton onSelect={select} selected={selected} disabled={disabled} />
					{isSlugFungible ? (
						<>
							{formatNumber(fungibleAmount || data.amount)}
							{isUsedTransfer && (
								<UsedTransferBadge />
							)}
							{isValidTransfer === false && !isUsedTransfer && (
								<UsedTransferBadge />
							)}
						</>
					) : (isItemFungible || isUsedTransfer) ? (
						<>
							<TooltipProvider delayDuration={200}>
								<Tooltip>
									<TooltipTrigger asChild>
										<div className="size-6 min-w-6 rounded overflow-hidden cursor-pointer">
											{isItemFungible ? (
												<CollectionIcon collection={data?.collection as any} className="size-6" imageClassName="size-6" />
											) : (
												<InscriptionContent inscription={data} className="size-6" />
											)}
										</div>
									</TooltipTrigger>
									<TooltipContent side="right" className="p-1 bg-background border border-border">
										<div className="size-40 rounded overflow-hidden">
											<InscriptionContent inscription={data} className="size-40" />
										</div>
									</TooltipContent>
								</Tooltip>
							</TooltipProvider>
							<span>{fungibleAmount ? formatNumber(fungibleAmount) : `Inscription #${data?.num}`}{collectionName ? ` ${collectionName}` : ''}</span>
							{(isUsedTransfer || isValidTransfer === false) && (
								<UsedTransferBadge />
							)}
						</>
					) : (
						<>
							<TooltipProvider delayDuration={200}>
								<Tooltip>
									<TooltipTrigger asChild>
										<div className="size-6 min-w-6 rounded overflow-hidden cursor-pointer">
											<InscriptionContent inscription={data} className="size-6" />
										</div>
									</TooltipTrigger>
									<TooltipContent side="right" className="p-1 bg-background border border-border">
										<div className="size-40 rounded overflow-hidden">
											<InscriptionContent inscription={data} className="size-40" />
										</div>
									</TooltipContent>
								</Tooltip>
							</TooltipProvider>
							{data?.meta?.name || `Inscription #${data?.num}`}
						</>
					)}
				</div>
				<div className="flex gap-2 items-center flex-wrap">{children}</div>
			</div>
		</CommandItem>
	)
}

export default BaseItem
